import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import InputDate from "../../../components/InputDate";
import InputTextarea from "../../../components/InputTextarea";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney, convertMoenyToInt } from "../../../helpers/conversions";
import { getRemainingAmount, isSettleable } from "../helpers";
import { usePaymentMethodOptions } from "../../../components/PaymentCondition/usePaymentOptions";

// Baixa (total ou parcial) de um título + estorno de baixas anteriores.
// Para títulos quitados vira o histórico: só a lista de baixas (com estorno),
// sem o formulário de nova baixa.
export const ModalSettle = ({ show, close, entry, bankAccounts, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const [reversingId, setReversingId] = useState(null);

    const [bankAccountId, setBankAccountId] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const { options: paymentMethodOptions, getLabel: getPaymentMethodLabel } = usePaymentMethodOptions(entry?.payment_method_id);
    const [amount, setAmount] = useState('R$ 0,00');
    const [interest, setInterest] = useState('R$ 0,00');
    const [discount, setDiscount] = useState('R$ 0,00');
    const [settledAt, setSettledAt] = useState('');
    const [comments, setComments] = useState('');

    const remaining = entry ? getRemainingAmount(entry) : 0;
    const historyOnly = entry ? !isSettleable(entry) : false;

    // Uma empresa pagar pela outra é caso legítimo (mútuo, rateio de despesa),
    // então isto avisa em vez de bloquear — o que não pode é acontecer por
    // acidente. Conta sem empresa definida não acusa nada: não dá para chamar
    // de divergente o que não se sabe.
    const selectedAccount = bankAccounts.find(account => account.id === bankAccountId);
    const crossCompanySettlement = !!entry?.billing_company_id
        && !!selectedAccount?.billing_company_id
        && selectedAccount.billing_company_id !== entry.billing_company_id;

    useEffect(() => {
        if (!show || !entry) return;

        // A conta de pagamento do título pré-preenche a baixa.
        setBankAccountId(
            entry.finance_bank_account_id
            ?? (bankAccounts.length === 1 ? bankAccounts[0].id : null)
        );
        // A forma prevista no título é só o ponto de partida: quem paga pode
        // ter pago de outro jeito, e é o que aconteceu que fica na baixa.
        setPaymentMethod(entry.payment_method_id ?? null);
        setAmount(convertIntToMoney(getRemainingAmount(entry), true));
        setInterest('R$ 0,00');
        setDiscount('R$ 0,00');
        setSettledAt(moment().format('YYYY-MM-DD'));
        setComments('');
    }, [show, entry]);

    const save = () => {
        setSaving(true);

        api.post(`/finance-entry/${entry.id}/settle`, {
            finance_bank_account_id: bankAccountId,
            payment_method_id: paymentMethod || null,
            amount: convertMoenyToInt(amount),
            interest: convertMoenyToInt(interest),
            discount: convertMoenyToInt(discount),
            settled_at: settledAt,
            comments: comments || null,
        }).then(() => {
            toast.success('Baixa registrada');
            close();
            onSaved();
        }).catch(renderError).then(() => setSaving(false));
    };

    const reverseSettlement = settlementId => {
        setReversingId(settlementId);

        api.delete(`/finance-entry/settlement/${settlementId}`).then(() => {
            toast.success('Baixa estornada');
            close();
            onSaved();
        }).catch(renderError).then(() => setReversingId(null));
    };

    const accountOptions = bankAccounts.map(account => ({ label: account.name, value: account.id }));

    return (
        <Modal
            title={historyOnly ? `Baixas do título` : `Baixar título`}
            show={show}
            close={close}
            footer={historyOnly
                ? <Button type={`secondary`} size={`small`} text={`Fechar`} action={close} />
                : <Button type={`primary`} size={`small`} text={`Registrar baixa`} action={save} loading={saving} disabled={!bankAccountId} />
            }
        >
            {entry &&
                <div className="section">
                    <div className="finance-settle-summary">
                        <div>
                            {entry.description}
                            <strong>{convertIntToMoney(entry.amount, true)}</strong>
                        </div>
                        <div>
                            Em aberto
                            <strong>{convertIntToMoney(remaining, true)}</strong>
                        </div>
                    </div>

                    {!historyOnly &&
                        <>
                            <div className="row">
                                <div className="col-6">
                                    <InputSelect menuInBody label={`Conta`} options={accountOptions} value={bankAccountId} change={setBankAccountId} />
                                    {crossCompanySettlement &&
                                        <span className={`finance-cross-company-warning`}>
                                            Esta conta é de outra empresa do que a do título. A baixa é registrada assim mesmo.
                                        </span>
                                    }
                                </div>
                                <div className="col-6">
                                    <InputDate label={`Data da baixa`} format={`YYYY-MM-DD`} value={settledAt} change={setSettledAt} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <InputSelect
                                        menuInBody
                                        label={`Forma de pagamento`}
                                        options={[{ label: 'Não informada', value: null }, ...paymentMethodOptions]}
                                        value={paymentMethod}
                                        change={setPaymentMethod}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-4">
                                    <Input label={`Valor ${entry.type === 'receivable' ? 'recebido' : 'pago'}`} value={amount} change={setAmount} mask={`convertToMoney`} />
                                </div>
                                <div className="col-4">
                                    <Input label={`Juros/multa`} value={interest} change={setInterest} mask={`convertToMoney`} />
                                </div>
                                <div className="col-4">
                                    <Input label={`Desconto`} value={discount} change={setDiscount} mask={`convertToMoney`} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <InputTextarea label={`Observações`} value={comments} change={setComments} rows={2} />
                                </div>
                            </div>
                        </>
                    }

                    {(entry.settlements ?? []).length > 0 &&
                        <div className="finance-settlements-list">
                            <h4>Baixas registradas</h4>
                            {entry.settlements.map(settlement => (
                                <div key={settlement.id} className="finance-settlement-row">
                                    <div className="settlement-info">
                                        {moment(settlement.settled_at).format('DD/MM/YYYY')}
                                        {settlement.payment_method_id && ` · ${getPaymentMethodLabel(settlement.payment_method_id)}`}
                                        {settlement.interest > 0 && ` · juros ${convertIntToMoney(settlement.interest, true)}`}
                                        {settlement.discount > 0 && ` · desconto ${convertIntToMoney(settlement.discount, true)}`}
                                    </div>
                                    <div className="settlement-value">{convertIntToMoney(settlement.amount, true)}</div>
                                    <Button
                                        type={`delete`}
                                        svg={`delete`}
                                        action={() => reverseSettlement(settlement.id)}
                                        loading={reversingId === settlement.id}
                                    />
                                </div>
                            ))}
                        </div>
                    }
                </div>
            }
        </Modal>
    );
};

export default ModalSettle;
